import { motion } from "framer-motion";
import { ArrowRight, Check, Quote } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import BeforeAfterChart from "@/components/site/BeforeAfterChart";
import { CASES, TESTIMONIALS, BRAND } from "@/lib/constants";

function CaseBlock({ item, index }) {
  const testimonial = item.testimonialId
    ? TESTIMONIALS.find((t) => t.id === item.testimonialId)
    : null;
  const isFirst = index === 0;

  return (
    <article
      className={`${isFirst ? "" : "pt-16 sm:pt-24 border-t border-black/10 mt-16 sm:mt-24"}`}
      data-testid={`case-block-${item.slug}`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-10 sm:mb-14"
        >
          <div className="lg:col-span-7">
            <p className="overline mb-5 text-[10px] sm:text-xs">
              <span className="inline-block w-8 sm:w-10 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
              {isFirst ? "Seneste case · Featured" : `Case ${String(index + 1).padStart(2, "0")}`}
            </p>
            <h2
              className="font-serif text-[2.25rem] sm:text-5xl lg:text-[4rem] text-[#111111] font-light tracking-tight leading-[1.05]"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              {item.name}
            </h2>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-[#555555]">
              <span className="tracking-wide">Overtaget {item.year}</span>
              <span className="text-[#D4AF37]">·</span>
              <span className="tracking-wide uppercase text-[10px] text-[#D4AF37]">
                {item.testimonialId ? "Ejerens befrielse" : "Fuld overtagelse"}
              </span>
            </div>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <p
              className="font-serif text-xl sm:text-2xl italic text-[#C9A227] font-light leading-snug"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              &ldquo;{item.tagline}&rdquo;
            </p>
          </div>
        </motion.div>

        {/* Gallery */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6">
          <div className="md:col-span-8 aspect-[4/3] overflow-hidden">
            <img src={item.gallery[0]} alt={`${item.name} - facade`} className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>
          <div className="md:col-span-4 aspect-[4/3] md:aspect-[3/4] overflow-hidden">
            <img src={item.gallery[1]} alt={`${item.name} - interiør`} className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>
          <div className="md:col-span-4 aspect-[4/3] overflow-hidden">
            <img src={item.gallery[2]} alt={`${item.name} - detalje`} className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>
          <div className="md:col-span-8 aspect-[4/3] overflow-hidden">
            <img src={item.gallery[3]} alt={`${item.name} - rum`} className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>
        </div>

        {/* Description + stats */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 mt-14 sm:mt-20">
          <div className="lg:col-span-7">
            <p className="overline mb-5 text-[10px] sm:text-xs">
              <span className="inline-block w-8 sm:w-10 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
              {item.testimonialId ? "Historien & resultaterne" : "Overtagelsen & resultaterne"}
            </p>
            <h3
              className="font-serif text-[1.75rem] sm:text-4xl text-[#111111] font-light tracking-tight leading-[1.1]"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              {item.summary}
            </h3>
            <div className="mt-8 text-[#333333] text-base sm:text-lg font-light leading-relaxed max-w-2xl space-y-4">
              {item.description.split("\n\n").map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            <ul className="mt-10 space-y-4">
              {item.highlights.map((h) => (
                <li key={h} className="flex items-start gap-4 text-[#333333] font-light leading-relaxed">
                  <Check size={20} className="text-[#D4AF37] mt-1 shrink-0" strokeWidth={1.5} />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>

          <aside className="lg:col-span-5">
            <div className="bg-[#F8F8F8] p-8 sm:p-10">
              <p className="overline mb-6 text-[10px] sm:text-xs">
                <span className="inline-block w-8 h-px bg-[#D4AF37] align-middle mr-3" />
                Resultater
              </p>
              <div className="space-y-8">
                {item.stats.map((s) => (
                  <div key={s.k} className="border-b border-black/10 pb-6 last:border-0 last:pb-0">
                    <div
                      className="font-serif text-3xl sm:text-4xl text-[#111111] font-light leading-none"
                      style={{ fontFamily: "Fraunces, serif" }}
                    >
                      {s.k}
                    </div>
                    <div className="text-[11px] tracking-[0.18em] uppercase text-[#555555] mt-3 leading-tight">
                      {s.v}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {item.beforeAfter && <BeforeAfterChart data={item.beforeAfter} />}

      {testimonial && (
        <section className="bg-[#111111] text-white py-16 sm:py-24 mt-16 sm:mt-24 relative overflow-hidden">
          <div className="absolute -top-32 -right-32 w-[36rem] h-[36rem] glow-gold pointer-events-none" />
          <div className="relative max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
            <Quote size={44} strokeWidth={1} className="text-[#D4AF37] mx-auto mb-8" />
            <blockquote
              className="font-serif text-[1.5rem] sm:text-[2.25rem] lg:text-[2.5rem] text-white font-light leading-[1.25] tracking-tight"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              &ldquo;{testimonial.quote}&rdquo;
            </blockquote>
            <div className="mt-10 flex items-center justify-center gap-4">
              {testimonial.photo ? (
                <img
                  src={testimonial.photo}
                  alt={testimonial.name}
                  loading="lazy"
                  decoding="async"
                  className="w-14 h-14 rounded-full object-cover ring-2 ring-[#D4AF37]/60 shrink-0"
                  data-testid={`case-testimonial-photo-${testimonial.id}`}
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C9A227] flex items-center justify-center shrink-0">
                  <span
                    className="font-serif text-2xl text-[#111111] font-medium"
                    style={{ fontFamily: "Fraunces, serif" }}
                  >
                    {testimonial.name.charAt(0)}
                  </span>
                </div>
              )}
              <div className="text-left">
                <div
                  className="font-serif text-xl text-white font-normal leading-tight"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  {testimonial.name}
                </div>
                <div className="text-xs text-white/60 mt-1 tracking-wide">{testimonial.role}</div>
              </div>
            </div>
          </div>
        </section>
      )}
    </article>
  );
}

export default function Cases() {
  return (
    <div className="bg-white text-[#222222]" data-testid="cases-page">
      <Header />

      <section className="relative bg-[#111111] text-white pt-28 sm:pt-36 pb-14 sm:pb-20 overflow-hidden">
        <div className="absolute -top-32 right-0 w-[36rem] h-[36rem] glow-gold pointer-events-none" />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.045] select-none">
          <span className="font-serif text-[26vw] leading-none italic" style={{ fontFamily: "Fraunces, serif" }}>
            Cases
          </span>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="overline mb-5 text-[10px] sm:text-xs" style={{ color: "#D4AF37" }}>
            <span className="inline-block w-8 sm:w-10 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
            Vores boliger
          </p>
          <h1
            className="font-serif text-[2.5rem] sm:text-6xl lg:text-[6rem] text-white font-light tracking-tight leading-[1.02] max-w-5xl"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            Cases fra <span className="italic text-[#D4AF37]">virkeligheden</span>.
          </h1>
          <p className="mt-6 text-white/70 text-base sm:text-lg font-light leading-relaxed max-w-2xl">
            Ejerne bag disse boliger valgte at give slip på driften. Se historierne
            om, hvad der ændrede sig - og de resultater vi skabte.
          </p>
        </div>
      </section>

      <section className="bg-white pt-14 sm:pt-20 pb-16 sm:pb-24">
        {CASES.map((c, i) => (
          <CaseBlock key={c.slug} item={c} index={i} />
        ))}
      </section>

      <section className="bg-[#F8F8F8] py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="overline mb-5 text-[10px] sm:text-xs">
                <span className="inline-block w-8 sm:w-10 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
                Din bolig, næste case
              </p>
              <h2
                className="font-serif text-[2rem] sm:text-4xl lg:text-5xl text-[#111111] font-light tracking-tight leading-[1.1]"
                style={{ fontFamily: "Fraunces, serif" }}
              >
                Skal din bolig være vores <span className="italic text-[#C9A227]">næste succes?</span>
              </h2>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
              <a
                href={BRAND.calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cases-book-btn"
                className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#111111] px-6 py-4 text-xs uppercase tracking-[0.18em] hover:bg-[#111111] hover:text-white transition-colors duration-500"
              >
                <span>Book en samtale</span>
                <ArrowRight size={16} strokeWidth={1.5} />
              </a>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-3 border border-[#111111] text-[#111111] px-6 py-4 text-xs uppercase tracking-[0.18em] hover:bg-[#111111] hover:text-white transition-colors duration-500"
              >
                <span>Skriv til os</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
